<template>
  <!-- Table row mode -->
  <tr v-if="tableMode" class="hover:bg-gray-50">
    <td class="px-6 py-4 whitespace-nowrap">
      <div class="flex items-center">
        <BaseInput v-if="allowActions"
                   v-model="newTitle"
                   class="edit-input"
                   :class="{'--error':$v.newTitle.$error }"
                   @keypress.enter="save"/>
        <span v-else class="text-base font-medium text-gray-900">
          {{ tag.title }}
        </span>
      </div>
    </td>
    <td class="px-6 py-4 whitespace-nowrap">
      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
        {{ tag.popularity || 0 }}
      </span>
    </td>
    <td v-if="allowActions" class="px-3 py-4 whitespace-nowrap text-center text-base font-medium w-24">
      <div class="flex items-center justify-end space-x-2">
        <transition name="slide">
          <div v-if="displayActions" class="flex items-center space-x-2">
            <button @click="save" class="text-green-600 hover:text-green-900">
              <font-awesome-icon icon="check" class="w-5 h-5"/>
            </button>
            <button @click="cancel" class="text-red-600 hover:text-red-900">
              <font-awesome-icon icon="times" class="w-5 h-5"/>
            </button>
          </div>
        </transition>
        <ArchiveBtn v-if="allowActions"
                     buttons-text-type="del-cancel"
                     :submitted="submitted"
                     @confirm="handleDelete">
          <template #icon>
            <IconTrash class="w-5 h-5 cursor-pointer text-red-600 hover:text-red-900 transition duration-200"/>
          </template>
          <template #popover>
            <p class="text-text text-lg font-bold leading-9" v-html="$t('removePopoverTitle')"></p>
            <p class="text-text text-2sm" v-if="tag.popularity">
              {{ $tc('removePopoverText', tag.popularity) }}
            </p>
          </template>
        </ArchiveBtn>
      </div>
    </td>
  </tr>
  
  <!-- List mode (original) -->
  <div v-else class="flex items-center group py-4">
    <div class="text-text-secondary text-sm mr-8 flex items-center">
      <BaseInput v-if="allowActions"
                 v-model="newTitle"
                 class="edit-input"
                 :class="{'--error':$v.newTitle.$error }"
                 @keypress.enter="save"/>
      <div v-else class="flex items-center">
        <span>{{ tag.title }}</span>
        <span class="ml-2 px-2 py-1 bg-text-ternary text-white text-xs rounded-full transition-all duration-200 hover:bg-text-secondary" :title="$tc('removePopoverText', tag.popularity || 0)">
          {{ tag.popularity || 0 }}
        </span>
      </div>
      
      <!-- Show counter next to input when editing -->
      <span v-if="allowActions" class="ml-2 px-2 py-1 bg-text-ternary text-white text-xs rounded-full transition-all duration-200 hover:bg-text-secondary" :title="$tc('removePopoverText', tag.popularity || 0)">
        {{ tag.popularity || 0 }}
      </span>
    </div>
    <transition name="slide">
      <div v-if="displayActions" class="actions-block">
        <font-awesome-icon class="text-lg mr-2 text-green cursor-pointer" icon="check"
                           @click="save"/>
        <font-awesome-icon class="text-lg text-red cursor-pointer" icon="times"
                           @click="cancel"/>
      </div>
    </transition>
    <ArchiveBtn v-if="allowActions"
                 buttons-text-type="del-cancel"
                 :submitted="submitted"
                 @confirm="handleDelete">
      <template #icon>
        <IconTrash class="cursor-pointer text-icon hover:text-icon-hover transition duration-200"/>
      </template>
      <template #popover>
        <p class="text-text text-lg font-bold leading-9" v-html="$t('removePopoverTitle')"></p>
        <p class="text-text text-2sm" v-if="tag.popularity">
          {{ $tc('removePopoverText', tag.popularity) }}
        </p>
      </template>
    </ArchiveBtn>
  </div>
</template>

<script>
import {mapActions, mapMutations} from "vuex";
import {helpers, required} from "vuelidate/lib/validators";
import IconEdit from "@/components/icons/IconEdit";
import IconTrash from "@/components/icons/IconTrash";
import BaseInput from "@/components/UI/BaseInput/BaseInput";
import ArchiveBtn from "@/components/UI/ArchiveBtn";

const noSpecSymbols = helpers.regex('noSpecSymbols', /^[^*|\/":<>[\]{}`\\()';@&!$]+$/)

export default {
  name: "TagListItem",
  components: {ArchiveBtn, BaseInput, IconTrash, IconEdit},
  props: {
    tag: {
      type: Object
    },
    allowActions: {
      type: Boolean
    },
    tableMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      newTitle: '',
      submitted: false
    }
  },
  computed: {
    displayActions() {
      return this.allowActions && this.newTitle !== this.tag.title
    },
    isEditing() {
      return this.allowActions && this.newTitle !== '' && this.newTitle !== this.tag.title
    }
  },
  validations: {
    newTitle: {
      required,
      noSpecSymbols
    }
  },
  methods: {
    ...mapActions('space/tags', ['updateTag', 'deleteTag']),
    ...mapMutations('space/tags', ['replaceTagInList', 'removeTagFromList']),
    async save() {
      if (this.submitted) return;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          this.submitted = true;
          const data = {
            id: this.tag.id,
            title: this.newTitle
          }
          const res = await this.updateTag(data);
          if (res.body.success) {
            this.$toasted.success(this.$t('savedSuccessfully'))
            this.replaceTagInList(res.body.data)
          } else {
            this.$toasted.error(this.$t('errors.response'))
          }
        } catch (err) {
          this.$toasted.error(this.$t('errors.response'))
          console.error(err)
        } finally {
          this.submitted = false;
        }
      }
    },
    cancel() {
      if (this.submitted) return;
      this.newTitle = this.tag.title;
    },
    async handleDelete() {
      try {
        this.submitted = true;
        const {id} = this.tag;
        const res = await this.deleteTag(id);
        if (res.body.success) {
          this.$toasted.success(this.$t('tagRemoved'))
          this.removeTagFromList(id);
        } else {
          this.$toasted.error(this.$t('errors.response'))
        }
      } catch (err) {
        this.$toasted.error(this.$t('errors.response'))
        console.error(err);
      } finally {
        this.submitted = false;
      }
    }
  },
  mounted() {
    this.newTitle = this.tag.title;
  },
  i18n: {
    messages: {
      en: {
        savedSuccessfully: 'Tag is updated',
        tagRemoved: 'Tag removed',
        removePopoverTitle: 'Are you sure you want to <br>remove the tag?',
        removePopoverText: '| It\'s assigned to {count} operation. | It\'s assigned to {count} operations.',
      },
      ru: {
        savedSuccessfully: 'Тег обновлен',
        tagRemoved: 'Тег удален',
        removePopoverTitle: 'Вы действительно хотите <br>удалить тег?',
        removePopoverText: '| Он присвоен {count} операции. | Он присвоен {count} операциям.',
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.edit-input {
  ::v-deep input {
    height: 2rem;
  }
}

.actions-block {
  @apply flex col-gap-4 justify-end pr-8;
  width: 6.2rem;
  overflow: hidden;
  transition: all .2s;
}

.slide-enter, .slide-leave-to {
  width: 0;
  padding: 0;
}
</style>